import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Sparkles, X } from 'lucide-react'
import { learningApi } from '@/api/learning'
import { Button } from '@/components/ui/Button'

interface MiniSessionPromptProps {
  /** When true, skip prompt if user is in a guided learning flow */
  inGuidedFlow?: boolean
}

export function MiniSessionPrompt({ inGuidedFlow = false }: MiniSessionPromptProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: status } = useQuery({
    queryKey: ['exploreSession'],
    queryFn: () => learningApi.getExploreSessionStatus(),
    refetchInterval: 30_000,
    enabled: !inGuidedFlow,
  })

  const dismissMutation = useMutation({
    mutationFn: () => learningApi.dismissExploreSession(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exploreSession'] }),
  })

  const startMutation = useMutation({
    mutationFn: () => learningApi.startMiniSession(),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['exploreSession'] })
      navigate(`/learn/${session.id}`)
    },
  })

  if (inGuidedFlow || !status?.shouldPromptMiniSession) return null

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl border bg-card p-4 shadow-xl sm:left-auto sm:right-6"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      data-testid="mini-session-prompt"
    >
      <button
        type="button"
        onClick={() => dismissMutation.mutate()}
        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-foreground">
              You&apos;ve found {status.wordsAdded} new words
            </p>
            <p className="text-xs text-muted-foreground">
              Quick review before you finish? (~2 min)
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="min-h-12 w-full text-sm sm:h-8 sm:w-auto sm:text-xs"
              loading={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              Quick review
            </Button>
            <Button
              className="min-h-12 w-full bg-secondary text-sm text-secondary-foreground hover:bg-secondary/80 sm:h-8 sm:w-auto sm:text-xs"
              onClick={() => dismissMutation.mutate()}
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
